// js/flacdium-api.js
// Adapter replacing the TIDAL backend with the Flacdium shared-library JSON API.
// Every LosslessAPI data request funnels through fetchWithRetry(); in Flacdium mode
// we intercept the relative path here and return a Response-like object whose .json()
// yields data in the exact shape LosslessAPI already parses (search/album/artist/track).
//
// Playback: each mapped track carries `audioUrl` (= /preview/{id}?token=…), so player.js
// takes its native direct-audio path (bit-perfect FLAC), bypassing HLS/Shaka/manifests.

import { buildAlbumGroups, buildArtistGroups, normalizeArtistKey, primaryArtistKey } from './artist-grouping.js';

const TOKEN_KEY = 'flacdium_token';
// Stable per-browser id sent with the sign-in request. The server's device-limit gate needs an
// identity for every client; the native app sends an `X-Device-Id` header, which a page in a
// browser cannot do. Persisting it here (rather than leaning only on the server's
// `flacdium_device` cookie) keeps the same browser on ONE device slot even when the cookie jar
// is cleared or the page is served cross-origin, where the cookie never rides along.
const DEVICE_KEY = 'flacdium_device_id';
// Last known signed-in identity, persisted so a transient /api/auth/me failure can keep
// the user logged in optimistically instead of dropping them to the auth gate.
const USER_KEY = 'flacdium_user';

function setStoredUser(me) {
    try {
        if (me && me.username) {
            localStorage.setItem(USER_KEY, JSON.stringify({ username: me.username, is_admin: !!me.is_admin }));
        } else {
            localStorage.removeItem(USER_KEY);
        }
    } catch {
        /* storage unavailable */
    }
}

function getStoredUser() {
    try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function isFlacdiumMode() {
    return typeof window !== 'undefined' && window.FLACDIUM_MODE !== false;
}

export function flacdiumBase() {
    // Same-origin deploy: flacdium-config.js sets FLACDIUM_BASE to '' and nginx proxies
    // /api,/preview,/covers,/download to the backend. Empty string must stay empty (relative
    // URLs, page scheme inherited — no mixed content). Only fall back to localhost when the
    // runtime config is entirely absent (undefined), e.g. opening the file without Docker.
    const configured = typeof window !== 'undefined' ? window.FLACDIUM_BASE : undefined;
    const base = configured == null ? 'http://localhost:8000' : configured;
    return String(base).replace(/\/+$/, '');
}

export function getToken() {
    try {
        return localStorage.getItem(TOKEN_KEY) || '';
    } catch {
        return '';
    }
}

export function setToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch {
        /* storage unavailable */
    }
}

// --- id encoding for album/artist derived from tag names (UTF-8 safe base64url) ---
function b64encode(str) {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64decode(value) {
    let padded = value.replace(/-/g, '+').replace(/_/g, '/');
    // b64encode() strips trailing '=' (matches the backend's `.rstrip("=")`), so ids embedded
    // in URLs never carry padding. Modern atob() is forgiving of missing padding, but re-adding
    // it defensively avoids relying on that leniency across bundler/runtime atob implementations
    // — a decode failure here silently degraded the artist page to "Unknown Artist".
    const remainder = padded.length % 4;
    if (remainder === 2) padded += '==';
    else if (remainder === 3) padded += '=';
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
}
function albumId(f) {
    return 'al_' + b64encode(JSON.stringify({ al: f.album || '', ar: f.album_artist || f.artist || '' }));
}
function artistId(f) {
    return 'ar_' + b64encode(f.artist || '');
}
function decodeAlbumId(id) {
    try {
        return JSON.parse(b64decode(String(id).replace(/^al_/, '')));
    } catch {
        return null;
    }
}
function decodeArtistId(id) {
    try {
        return b64decode(String(id).replace(/^ar_/, ''));
    } catch {
        return null;
    }
}

function qualityFor(f) {
    return (f.bit_depth || 0) > 16 || (f.sample_rate || 0) > 48000 ? 'HI_RES_LOSSLESS' : 'LOSSLESS';
}

// Flacdium track JSON -> TIDAL-ish raw track consumed by LosslessAPI.prepareTrack().
export function mapTrack(f) {
    const base = flacdiumBase();
    const token = getToken();
    const cover = f.cover_url ? base + f.cover_url : null;
    // Auth via `?token=` in the URL — the reliable path for the `<audio>` element (a cookie
    // is subject to a boot race where playback can start before /api/auth/me sets it).
    const tokenQuery = token ? '?token=' + encodeURIComponent(token) : '';
    const stream = base + (f.stream_url || '/preview/' + f.id) + tokenQuery;
    const releaseDate = f.release_date || (f.year ? String(f.year) + '-01-01' : undefined);
    const artist = { id: artistId(f), name: f.artist || 'Unknown Artist', type: 'ARTIST', picture: null };
    return {
        id: f.id,
        title: f.title || 'Untitled',
        duration: f.duration_seconds || 0,
        trackNumber: f.track_number || 1,
        volumeNumber: f.disc_number || 1,
        audioQuality: qualityFor(f),
        explicit: false,
        copyright: '',
        isrc: '',
        popularity: 0,
        replayGain: 0,
        peak: 1,
        type: 'track',
        url: '',
        streamStartDate: releaseDate,
        artist,
        artists: [artist],
        album: { id: albumId(f), title: f.album || 'Unknown Album', cover, releaseDate },
        // Flacdium-specific: native direct-audio path in player.js (bit-perfect FLAC).
        audioUrl: stream,
        isFlacdium: true,
        flacSpecs: f.specs_label || '',
        // Native source specs (from backend serialize_api_track) — used by the bit-perfect
        // badge to show real rate/bit depth and warn when the OS will resample.
        sampleRate: f.sample_rate || 0,
        bitDepth: f.bit_depth || 0,
        channels: f.channels || 0,
        downloadUrl: base + (f.download_url || '/download/' + f.id),
    };
}

function dedupeAlbums(tracks) {
    const map = new Map();
    for (const t of tracks) {
        const a = t.album;
        if (!a) continue;
        const existing = map.get(a.id);
        if (existing) {
            existing.numberOfTracks += 1;
            continue;
        }
        map.set(a.id, {
            id: a.id,
            title: a.title,
            cover: a.cover,
            releaseDate: a.releaseDate,
            artist: t.artist,
            artists: [t.artist],
            numberOfTracks: 1,
            type: 'ALBUM',
        });
    }
    return Array.from(map.values());
}

function dedupeArtists(tracks) {
    const map = new Map();
    for (const t of tracks) {
        const a = t.artist;
        if (!a || map.has(a.id)) continue;
        map.set(a.id, { id: a.id, name: a.name, picture: null, type: 'ARTIST' });
    }
    return Array.from(map.values());
}

// In-flight request dedup: collapses concurrent identical GETs (e.g. getArtist fires
// /artist/?id= and /artist/?f= in parallel — both map to the same /api/tracks query).
const _inflight = new Map();

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Single fetch attempt with a hard timeout so a hung connection can't stall the UI forever.
async function _fetchOnce(url, headers, timeoutMs = 15000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { headers, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

// GET JSON with retry + backoff. A transient network drop (fetch throws "Failed to fetch")
// or a Cloudflare/backend hiccup (429/5xx) is retried a few times so the app self-heals
// instead of surfacing an error that only a manual F5 clears. Genuine 4xx (auth, not-found)
// fail fast — retrying those is pointless.
async function apiGet(path) {
    if (_inflight.has(path)) return _inflight.get(path);
    const promise = (async () => {
        const token = getToken();
        const headers = {};
        if (token) headers['Authorization'] = 'Bearer ' + token;
        const url = flacdiumBase() + path;
        const maxAttempts = 4;
        let lastErr;
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            try {
                const res = await _fetchOnce(url, headers);
                if (res.ok) return await res.json();
                // Retry only transient server states; other 4xx are real and final.
                if (![429, 500, 502, 503, 504].includes(res.status) || attempt === maxAttempts) {
                    throw new Error('Flacdium API ' + res.status + ' for ' + path);
                }
            } catch (err) {
                lastErr = err;
                if (attempt === maxAttempts) throw err;
            }
            await _sleep(400 * 2 ** (attempt - 1)); // 0.4s, 0.8s, 1.6s
        }
        throw lastErr || new Error('Flacdium API request failed for ' + path);
    })();
    _inflight.set(path, promise);
    try {
        return await promise;
    } finally {
        _inflight.delete(path);
    }
}

// Full-library browse (the landing view for a small shared library). Returns mapped-but-
// not-prepared raw tracks; the caller runs them through LosslessAPI.prepareTrack().
export async function flacdiumLibraryRaw(page = 1, sort = 'newest') {
    const data = await apiGet(
        '/api/tracks?per_page=60&tracks_page=' + page + '&sort=' + encodeURIComponent(sort)
    );
    return {
        items: (data.items || []).map(mapTrack),
        total: data.total_items ?? 0,
        hasNext: !!data.has_next,
        page: data.page ?? page,
        totalPages: data.total_pages ?? 1,
    };
}

// Whole-library browse: artists grid (paginated, independent of any track page). Backend
// artist id is `ar_<b64url(name)>` — identical to artistId() above — so cards route through
// the existing /artist/<id> path with zero router changes.
export async function flacdiumArtistsRaw(page = 1, sort = 'name', q = '', letter = '') {
    const params = new URLSearchParams({ page: String(page), per_page: '60', sort });
    if (q) params.set('q', q);
    if (letter) params.set('letter', letter);
    const data = await apiGet('/api/artists?' + params.toString());
    const base = flacdiumBase();
    return {
        items: (data.items || []).map((a) => ({
            id: a.id,
            name: a.name || 'Unknown Artist',
            // Backend has no dedicated artist photo; a representative album cover keeps the
            // card grid from going blank. picture carries the absolute URL so getArtistPictureUrl
            // (which passes http(s) URLs through untouched) renders it directly.
            picture: a.cover_url ? base + a.cover_url : null,
            _tracks: a.tracks || 0,
        })),
        total: data.total_items ?? 0,
        hasNext: !!data.has_next,
        page: data.page ?? page,
        totalPages: data.total_pages ?? 1,
    };
}

// Whole-library browse: artists grid GROUPED like the app's Artists tab — one card per
// primary artist, feat/case/diacritic variants merged. Loads every raw page first (bounded:
// a handful of 60-item pages for a typical library) then collapses via buildArtistGroups,
// so the whole grouped set comes back as a single page (the grid stops paging after it).
// Page 1 is fetched alone to learn the total page count, then the rest are fetched
// concurrently in bounded batches — a serial one-page-at-a-time loop here left the tab
// showing skeletons until the whole library had downloaded.
export async function flacdiumArtistsGrouped(sort = 'name', q = '', letter = '') {
    const first = await flacdiumArtistsRaw(1, sort, q, letter);
    const all = [...first.items];
    const totalPages = first.totalPages || 1;
    if (first.hasNext && totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const BATCH_SIZE = 6;
        for (let i = 0; i < remainingPages.length; i += BATCH_SIZE) {
            const batch = remainingPages.slice(i, i + BATCH_SIZE);
            const chunks = await Promise.all(batch.map((p) => flacdiumArtistsRaw(p, sort, q, letter)));
            for (const chunk of chunks) all.push(...chunk.items);
        }
    }
    const grouped = buildArtistGroups(all, (name) => 'ar_' + b64encode(name));
    return { items: grouped, total: grouped.length, hasNext: false, page: 1, totalPages: 1 };
}

// Whole-library browse: albums/EPs grid (paginated). Backend album id is
// `al_<b64url({al,ar})>` — identical to albumId() above — so cards route through the
// existing /album/<id> path with zero router changes. `type` filters ep|album|all.
export async function flacdiumAlbumsRaw(page = 1, sort = 'recent', q = '', letter = '', type = 'all') {
    const params = new URLSearchParams({ page: String(page), per_page: '60', sort, type });
    if (q) params.set('q', q);
    if (letter) params.set('letter', letter);
    const data = await apiGet('/api/albums?' + params.toString());
    const base = flacdiumBase();
    return {
        items: (data.items || []).map((a) => ({
            id: a.id,
            title: a.album || 'Unknown Album',
            cover: a.cover_url ? base + a.cover_url : null,
            artist: a.artist || 'Unknown Artist',
            type: a.is_ep ? 'EP' : 'ALBUM',
            numberOfTracks: a.tracks || 0,
        })),
        total: data.total_items ?? 0,
        hasNext: !!data.has_next,
        page: data.page ?? page,
        totalPages: data.total_pages ?? 1,
    };
}

// Whole-library browse: albums/EPs grid GROUPED like the app — one card per (album name +
// primary artist), so a title split across feat/collab credits ("Thang", "Thang, Ha Le", …)
// is one card while a same-named album by a different artist stays separate. Loads every raw
// page first (bounded) then collapses; the whole grouped set returns as a single page. Page 1
// is fetched alone to learn the total page count, then the rest are fetched concurrently in
// bounded batches (see flacdiumArtistsGrouped above for why).
export async function flacdiumAlbumsGrouped(sort = 'recent', q = '', letter = '', type = 'all') {
    const first = await flacdiumAlbumsRaw(1, sort, q, letter, type);
    const all = [...first.items];
    const totalPages = first.totalPages || 1;
    if (first.hasNext && totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const BATCH_SIZE = 6;
        for (let i = 0; i < remainingPages.length; i += BATCH_SIZE) {
            const batch = remainingPages.slice(i, i + BATCH_SIZE);
            const chunks = await Promise.all(batch.map((p) => flacdiumAlbumsRaw(p, sort, q, letter, type)));
            for (const chunk of chunks) all.push(...chunk.items);
        }
    }
    const grouped = buildAlbumGroups(all, (al, ar) => 'al_' + b64encode(JSON.stringify({ al: al || '', ar: ar || '' })));
    return { items: grouped, total: grouped.length, hasNext: false, page: 1, totalPages: 1 };
}

// Library-wide counts for the browse header ("120 artists · 340 albums · 5200 songs").
export function flacdiumSummary() {
    return apiGet('/api/summary');
}

// Best-effort artist photo (Flacdium stores none): backend resolves + caches via Deezer.
// Returns '' when nothing is found — callers keep whatever placeholder they already have.
export async function flacdiumArtistImage(name) {
    if (!name) return '';
    try {
        const data = await apiGet('/api/artist-image?name=' + encodeURIComponent(name));
        return data.url || '';
    } catch {
        return '';
    }
}

async function searchData(term) {
    const data = await apiGet('/api/tracks?per_page=50&q=' + encodeURIComponent(term || ''));
    const items = (data.items || []).map(mapTrack);
    const empty = { items: [], limit: 0, offset: 0, totalNumberOfItems: 0 };
    return {
        tracks: { items, limit: items.length, offset: 0, totalNumberOfItems: data.total_items ?? items.length },
        albums: wrap(dedupeAlbums(items)),
        artists: wrap(dedupeArtists(items)),
        playlists: empty,
        videos: empty,
    };
}

function wrap(items) {
    return { items, limit: items.length, offset: 0, totalNumberOfItems: items.length };
}

async function albumData(id) {
    const key = decodeAlbumId(id) || { al: '', ar: '' };
    // Fetch by album name only (backend does a substring LIKE), then group client-side by
    // EXACT album name + the album's PRIMARY artist — so an album whose tracks carry
    // different/feat/split artist credits (e.g. one song split into two feat entries) shows
    // every track, while a same-named album by a genuinely different artist stays separate.
    const data = await apiGet('/api/tracks?per_page=100&album=' + encodeURIComponent(key.al));
    const albumKey = normalizeArtistKey(key.al);
    let items = (data.items || []).map(mapTrack).filter((t) => normalizeArtistKey(t.album.title) === albumKey);
    if (key.ar) {
        const targetKey = primaryArtistKey(key.ar);
        const matchArtist = items.filter((t) => primaryArtistKey(t.artist.name) === targetKey);
        if (matchArtist.length) items = matchArtist;
    }
    items.sort((a, b) => (a.volumeNumber - b.volumeNumber) || (a.trackNumber - b.trackNumber));
    const first = items[0];
    return {
        id,
        title: key.al || (first && first.album.title) || 'Unknown Album',
        cover: first ? first.album.cover : null,
        releaseDate: first ? first.album.releaseDate : undefined,
        artist: first ? first.artist : undefined,
        numberOfTracks: items.length,
        type: 'ALBUM',
        items,
    };
}

async function artistData(id) {
    const name = decodeArtistId(id) || '';
    // Backend `artist=` is a substring LIKE, so it returns tracks where this name appears
    // anywhere (primary OR feat). Keep only tracks where it's the PRIMARY (first) credit —
    // the app's rule "one entry per song, by its main artist; feat credits don't spawn a
    // separate artist" — so a grouped artist's page absorbs their "Name, Guest" feat tracks
    // but a track where they're merely a guest doesn't leak onto their page.
    const data = await apiGet('/api/tracks?per_page=100&artist=' + encodeURIComponent(name));
    const targetKey = primaryArtistKey(name);
    const items = (data.items || []).map(mapTrack).filter((t) => primaryArtistKey(t.artist.name) === targetKey);
    return {
        artist: { id, name: name || 'Unknown Artist', picture: null, type: 'ARTIST' },
        albums: dedupeAlbums(items),
        tracks: items,
    };
}

async function trackData(id) {
    const f = await apiGet('/api/tracks/' + encodeURIComponent(id));
    const track = mapTrack(f);
    // parseTrackLookup wants [trackWithDuration, infoWithManifest]; manifest carries the
    // direct FLAC url so getStreamUrl works even if the direct path is ever bypassed.
    const info = { manifest: { urls: [track.audioUrl] }, manifestMimeType: 'application/json', audioQuality: track.audioQuality };
    return [track, info];
}

// --- auth (shared Flacdium accounts) ---
function getDeviceId() {
    try {
        let id = localStorage.getItem(DEVICE_KEY);
        if (!id) {
            id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2))
                .replace(/[:/]/g, '');
            localStorage.setItem(DEVICE_KEY, id);
        }
        return id;
    } catch {
        // Storage unavailable (private mode / blocked): send nothing and let the server fall
        // back to its own cookie identity rather than failing the sign-in.
        return '';
    }
}

async function authPost(path, username, password, extra = {}) {
    const res = await fetch(flacdiumBase() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, device_id: getDeviceId(), ...extra }),
    });
    let data = {};
    try {
        data = await res.json();
    } catch {
        /* non-json error */
    }
    if (!res.ok) throw new Error(data.detail || ('Request failed (' + res.status + ')'));
    setToken(data.token);
    setStoredUser({ username: data.username, is_admin: data.is_admin });
    return { username: data.username };
}

export function flacdiumLogin(username, password) {
    return authPost('/api/auth/login', username, password);
}
export async function flacdiumMe() {
    const token = getToken();
    if (!token) return null; // no token → no network call on boot
    try {
        const res = await fetch(flacdiumBase() + '/api/auth/me', {
            headers: { Authorization: 'Bearer ' + token },
        });
        if (!res.ok) {
            // Only a definitive auth failure (401/403) invalidates the session. A transient
            // proxy/server blip (429/500/502/503, e.g. a backend restart) must NOT wipe a
            // token the server still honours for 30 days — otherwise a momentary hiccup
            // silently logs the user out ("văng phiên"). On those, keep the token and fall
            // back to the last known identity so the auth gate stays down.
            if (res.status === 401 || res.status === 403) {
                setToken('');
                setStoredUser(null);
                return null;
            }
            return getStoredUser();
        }
        const me = await res.json();
        setStoredUser(me);
        return me;
    } catch {
        // Network unreachable (offline / DNS): keep the user signed in optimistically.
        return getStoredUser();
    }
}
export function flacdiumLogout() {
    setToken('');
    setStoredUser(null);
}

export function hasFlacdiumToken() {
    return !!getToken();
}

// The devices currently signed in to this account (device-limit anti-account-sharing).
// Bearer-authed like flacdiumMe. Returns the device array ([] for admins, who aren't tracked),
// or null when there's no token / the call fails — the caller just hides the section then.
export async function flacdiumAccountDevices() {
    const token = getToken();
    if (!token) return null;
    try {
        const res = await fetch(flacdiumBase() + '/api/account/devices', {
            headers: { Authorization: 'Bearer ' + token },
        });
        if (!res.ok) return null;
        const body = await res.json();
        return Array.isArray(body.devices) ? body.devices : [];
    } catch {
        return null;
    }
}

// Self-remove one of this account's devices (frees a slot). Enforces the server-side
// once-per-cooldown window; on a cooldown/again-later refusal the server returns a non-2xx
// with a Vietnamese message, surfaced here so the caller can show it. Resolves true on success.
export async function flacdiumRemoveDevice(deviceId) {
    const token = getToken();
    if (!token) return { ok: false, message: 'Chưa đăng nhập.' };
    try {
        const res = await fetch(flacdiumBase() + '/api/account/devices/' + encodeURIComponent(deviceId) + '/remove', {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + token },
        });
        if (res.ok) return { ok: true };
        let message = 'Không xóa được thiết bị.';
        try {
            const body = await res.json();
            if (body && body.detail) message = body.detail;
        } catch {
            /* non-JSON error body — keep the default message */
        }
        return { ok: false, message };
    } catch {
        return { ok: false, message: 'Lỗi mạng, thử lại sau.' };
    }
}

// Live stream URL for a track id using the CURRENT token. Called at play time so a track
// browsed before login still streams once the user signs in (token added on the fly).
export function flacdiumStreamUrl(id) {
    // Rebuild the /preview URL with the CURRENT token so a track browsed before sign-in
    // streams once the user logs in.
    const token = getToken();
    return flacdiumBase() + '/preview/' + id + (token ? '?token=' + encodeURIComponent(token) : '');
}

function responseLike(data) {
    return { ok: true, status: 200, json: async () => data, headers: new Map() };
}

// Route a LosslessAPI relative path to the Flacdium API. Returns a Response-like object,
// or null for paths Flacdium intentionally does not serve (recommendations/lyrics/etc).
export async function flacdiumRoute(relativePath) {
    const url = new URL(relativePath, 'http://local');
    const path = url.pathname.replace(/\/+$/, '') || '/';
    const sp = url.searchParams;
    if (path === '/search') {
        const term = sp.get('q') || sp.get('s') || sp.get('a') || sp.get('al') || sp.get('v') || sp.get('p') || '';
        return responseLike(await searchData(term));
    }
    if (path === '/album') {
        return responseLike(await albumData(sp.get('id')));
    }
    if (path === '/artist') {
        return responseLike(await artistData(sp.get('id') || sp.get('f')));
    }
    if (path === '/track') {
        return responseLike(await trackData(sp.get('id')));
    }
    // Unserved TIDAL-only routes (recommendations, similar, manifests, playlist, mix, video).
    return responseLike({ items: [], tracks: [], albums: [] });
}
